import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ClearColourIcon } from "../../icons";

const Swatches = [
  "#000000",
  "#434343",
  "#666666",
  "#999999",
  "#cccccc",
  "#ffffff",
  "#e03e2d",
  "#e67e23",
  "#f1c40f",
  "#2dc26b",
  "#3598db",
  "#843fa1",
];

/** A style's colour as `#rrggbb`, which is what a colour input takes; empty for anything else. */
export function toHex(value: string) {
  const rgb = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i.exec(value.trim());

  if (rgb) {
    return `#${rgb
      .slice(1, 4)
      .map((part) => Math.min(255, Number(part)).toString(16).padStart(2, "0"))
      .join("")}`;
  }

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());

  if (!hex) {
    return "";
  }

  return hex[1].length === 3 ? `#${hex[1].replace(/./g, "$&$&")}`.toLowerCase() : value.trim().toLowerCase();
}

interface RichTextColourPickerProps {
  label: string;
  clearLabel: string;
  icon: IconDefinition;
  initial: string;
  /** The colour the words at the caret have, which the picker then shows. */
  current: string;
  buttonClassName: string;
  onPick: (colour: string | null) => void;
}

/** A colour button that opens a few colours, a custom one, and the choice of none. */
export function RichTextColourPicker({
  label,
  clearLabel,
  icon,
  initial,
  current,
  buttonClassName,
  onPick,
}: RichTextColourPickerProps) {
  const holder = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [lastPicked, setColour] = useState(initial);
  const colour = current || lastPicked;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const close = (event: Event) => {
      if (!holder.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);

    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (chosen: string | null) => {
    if (chosen) {
      setColour(chosen);
    }

    onPick(chosen);
  };

  return (
    <div ref={holder} className="position-relative">
      <button
        type="button"
        className={classNames(buttonClassName, "flex-column gap-1")}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="true"
        title={label}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setOpen((current) => !current)}
      >
        <FontAwesomeIcon icon={icon} className="fa-lg" />
        <span className="d-block w-100 border" style={{ height: 3, background: colour }} />
      </button>
      {open && (
        <div className="dropdown-menu show p-2" style={{ minWidth: "11rem" }}>
          <div className="d-flex flex-wrap gap-1 mb-2">
            {Swatches.map((swatch) => (
              <button
                key={swatch}
                type="button"
                className="btn p-0 border rounded-1 w-32 h-32"
                style={{ background: swatch }}
                aria-label={swatch}
                title={swatch}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  pick(swatch);
                  setOpen(false);
                }}
              />
            ))}
          </div>
          <label className="d-flex align-items-center gap-2 mb-2 fs-7">
            <input
              type="color"
              aria-label={`Custom ${label.toLowerCase()}`}
              className="form-control form-control-color form-control-sm w-32 h-32 p-1"
              value={colour}
              onChange={(event) => pick(event.target.value)}
            />
            Custom
          </label>
          <button
            type="button"
            className="btn btn-sm btn-light w-100 d-flex align-items-center gap-2"
            aria-label={clearLabel}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              pick(null);
              setOpen(false);
            }}
          >
            <FontAwesomeIcon icon={ClearColourIcon} className="small" />
            {clearLabel}
          </button>
        </div>
      )}
    </div>
  );
}
